<template>
  <DialogContent>
    <ShieldIcon class="mx-auto size-6 mb-2.5 stroke-gray-800" />

    <div
      class="font-primary font-semibold text-[19px] leading-[26px] text-gray-800 mb-[18px] text-center"
    >
      Confirmation of right to add work to Dacr
    </div>

    <div class="font-secondary text-[11px] leading-[17px] text-gray-800 px-2">
      By continuing, I represent and warrant that I own or control the copyright
      (or exclusive rights) in the work(s) submitted, or I am duly authorized to
      submit them for the rights holder, and that Dacr may rely on this.
      Submitting work without rights or providing false information may be
      unlawful (including fraud) and may expose me to civil and criminal
      liability under applicable law.
    </div>

    <div class="w-full h-px bg-[#DFDFDF] my-[14px]"></div>

    <div
      v-if="originFlagEnabled"
      class="w-full rounded-xl bg-[#F6F6F6] border border-[#EEEDEC] pt-[7px] pb-[9px] px-[13px] mb-2"
    >
      <div
        class="font-secondary text-[13px] leading-[17px] font-medium text-gray-800 mb-2"
      >
        Selected jurisdiction to be registered
      </div>

      <div class="flex justify-between">
        <div class="flex">
          <div
            class="size-8 border border-gray-600/15 rounded-full flex items-center justify-center bg-white -mr-3 z-10"
          >
            <Icon name="Dacr" :size="24" />
          </div>
          <Avatar
            v-for="origin in additionalOrigins"
            :key="origin.id"
            :src="origin.flag.url"
            :size="35"
            class="-mr-3 z-10 border-3 border-[#F6F6F6]"
          />
          <div
            v-if="showPlusButton"
            class="size-8 flex items-center justify-center border border-dashed border-gray-300/50 hover:border-gray-300/75 rounded-full group cursor-pointer transition-all"
            @click="handleOriginClick"
          >
            <Icon
              name="Plus"
              :size="12"
              class="stroke-gray-400 group-hover:stroke-gray-500 transition-all"
            />
          </div>
        </div>
        <div
          v-if="originFlagEnabled"
          class="font-secondary font-medium text-[13px] leading-[18px] text-green-700 hover:text-green-600 cursor-pointer"
          @click="handleOriginClick"
        >
          Change
        </div>
      </div>
    </div>

    <template v-if="showPrices">
      <div
        class="w-full rounded-xl border border-[#EEEDEC] py-[7px] px-[13px] flex flex-col gap-y-1.5"
      >
        <div class="flex items-center justify-between">
          <div class="font-secondary text-[13px] leading-[18px] text-gray-600">
            Works being registered
          </div>

          <div class="font-secondary text-[14px] leading-[18px] text-gray-800">
            {{ props.uploadCount }}
          </div>
        </div>

        <div class="flex justify-between">
          <div class="flex items-center gap-x-2">
            <div
              class="font-secondary text-[13px] leading-[18px] text-gray-800 font-medium"
            >
              Total to be billed
            </div>
            <NewLink type="secondary" @click="handleDetailsClick"
              >Details</NewLink
            >
          </div>
          <div
            class="font-secondary font-medium text-[17px] leading-[18px] text-gray-800"
          >
            {{ totalCost }}
          </div>
        </div>
      </div>
    </template>

    <div class="flex items-start gap-x-2 mt-[14px]">
      <Checkbox v-model="agreeToTerms" :class="checkboxClasses" />
      <div
        class="-mt-0.5 font-secondary text-[12px] leading-[17px] text-gray-600"
      >
        I represent and warrant I have the rights/authorization to submit these
        work(s), and I confirm my continued acceptance of the
        <a class="text-link" href="/legal/terms" target="_blank"> Terms </a>
        and acknowledgment of the
        <span class="inline-flex">
          <a class="text-link" href="/legal/privacy" target="_blank">
            Privacy Policy
          </a>
          .
        </span>
      </div>
    </div>
  </DialogContent>
  <DialogFooter class="flex flex-col gap-2 mt-2.5!">
    <UiButton
      class="w-full"
      primary
      large
      :disabled="disableButton"
      @click="handleAgree"
    >
      Agree, continue
    </UiButton>
    <div class="font-secondary text-[14px] text-link" @click="close()">
      Cancel upload
    </div>
  </DialogFooter>
</template>

<script setup lang="ts">
import { Icon, UiButton, Checkbox, Avatar } from '@dacr/ui'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import NewLink from '@/shared/components/Link.vue'
import { computed, ref, watch } from 'vue'
import ShieldIcon from '@/assets/svg/icons/ShieldIcon.svg'
import { useOriginStore } from '@/shared/stores/origin'
import { useUserStore } from '@/shared/stores/user'
import { formatPrice } from '@/shared/utils/money'
import { graphql } from '@/shared/graphql/router'
import { useQuery } from '@vue/apollo-composable'
import type { EstimateRegistrationCostQuery } from '@/shared/graphql/router/graphql'
import { PlanType, UserPermission } from '@/shared/graphql/legacy/generated'
import { useBillingStore } from '@/features/billing/store'
import { useFeatureFlag } from '@/shared/composables/useFeatureFlag'
import { FeatureFlag } from '@/shared/stores/featureFlags'

const props = defineProps<{
  uploadCount: number
}>()

const { close, triggerEvent } = useDialog()
const { open } = useDialog()

const originStore = useOriginStore()
const userStore = useUserStore()
const userOrigin = userStore.user?.origin
const billingStore = useBillingStore()
const originFlagEnabled = useFeatureFlag(FeatureFlag.Origin)

const selectedOriginIds = ref<string[]>(
  userOrigin && userOrigin !== 'Dacr' ? ['Dacr', userOrigin] : ['Dacr']
)

const selectedOrigins = computed(() =>
  originStore.origins.filter((o) => selectedOriginIds.value.includes(o.id))
)

const additionalOrigins = computed(() =>
  selectedOrigins.value.filter((o) => o.id !== 'Dacr')
)

const showPlusButton = computed(
  () => selectedOriginIds.value.length === 1 && originFlagEnabled.value
)

type EstimatedCost = EstimateRegistrationCostQuery['estimateRegistrationCost']

const estimatedCost = ref<EstimatedCost>(null)

const { result: estimateResult } = useQuery(
  graphql(`
    query EstimateRegistrationCostInitial(
      $input: EstimateRegistrationCostInput!
    ) {
      estimateRegistrationCost(input: $input) {
        total {
          amount
          formatted
        }
        breakdown {
          description
          origin {
            id
            name
            flag {
              url
            }
          }
          total {
            amount
            formatted
          }
          breakdown {
            id
            name
            total {
              amount
              formatted
            }
          }
        }
      }
    }
  `),
  () => ({
    input: {
      origins: [...selectedOriginIds.value],
      quantity: props.uploadCount,
    },
  }),
  {
    clientId: 'router',
  }
)

watch(
  () => estimateResult.value?.estimateRegistrationCost,
  (cost) => {
    if (cost) estimatedCost.value = cost
  },
  { immediate: true }
)

const showPrices = computed(
  () => billingStore.billing?.plan.type === PlanType.Pro
)

const totalCost = computed(() => {
  return formatPrice({ amount: estimatedCost.value?.total.amount || 0 })
})

const agreeToTerms = ref(false)

const checkboxClasses = computed(() => ({
  'border-green-500 outline-2 outline-green-200/50 rounded-sm':
    !agreeToTerms.value,
}))

const disableButton = computed(
  () => !agreeToTerms.value || (showPrices.value && !estimatedCost.value)
)

const handleAgree = () => {
  triggerEvent('success', selectedOriginIds.value)
}

const handleDetailsClick = () => {
  open(Dialogs.ConfirmUploadDetails, {
    stack: true,
    props: {
      selectedOrigins: selectedOrigins.value,
      estimatedCost: estimatedCost.value,
    },
  })
}

const handleOriginClick = () => {
  open(Dialogs.AddOrigins, {
    stack: true,
    props: {
      selectedOriginIds: [...selectedOriginIds.value],
      quantity: props.uploadCount,
      lockedOriginId: userOrigin ?? null,
    },
    events: {
      success({
        ids,
        estimatedCost: cost,
      }: {
        ids: string[]
        estimatedCost: EstimatedCost
      }) {
        selectedOriginIds.value = ids
        if (cost) estimatedCost.value = cost
      },
    },
  })
}
</script>
